import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export type CanalNotification = 'IN_APP' | 'EMAIL' | 'SMS';
export type StatutAlerte = 'OUVERTE' | 'TRAITEE' | 'IGNOREE';

/** Délai ou seuil réglable d'une règle (TF-435), avec sa valeur en vigueur. */
export interface ReglageRegle {
  cle: string;
  libelle: string;
  unite: 'JOURS' | 'MOIS' | 'NOTE';
  uniteLibelle: string;
  min: number;
  max: number;
  defaut: number;
  valeur: number;
}

export interface RegleAlerte {
  code: string;
  libelle: string;
  /** Énoncé depuis les délais en vigueur quand la règle en porte. */
  declencheur: string;
  actif: boolean;
  canaux: CanalNotification[];
  destinataires: { profil: string; libelle: string }[];
  reglages: ReglageRegle[];
  sites: { code: string; actif: boolean }[];
  evalueeAutomatiquement: boolean;
  ouvertes: number;
}

export interface AlerteDeclenchee {
  id: string;
  regle: string;
  libelleRegle: string;
  message: string;
  statut: StatutAlerte;
  echeanceLe: string | null;
  declencheeLe: string;
  apprenantId: string | null;
  apprenant: string | null;
  siteCode: string | null;
}

export interface EtatAlertes {
  regles: RegleAlerte[];
  /** Page courante des dernières alertes déclenchées, pas la liste entière. */
  dernieres: AlerteDeclenchee[];
  pagination: {
    page: number;
    taille: number;
    total: number;
    pages: number;
  };
  compteurs: {
    regles: number;
    activesSurPerimetre: number;
    declencheesAujourdHui: number;
    ouvertes: number;
    nonEvaluees: number;
  };
  /** La direction seule règle les alertes (TF-435) ; les pôles les lisent. */
  peutRegler: boolean;
}

/**
 * État du module d'alertes. La liste des alertes déclenchées est paginée côté
 * serveur : `page` et `taille` font partie de la clé de cache. `statut` ne
 * garde que les alertes de cet état (les ouvertes, pour un tableau de bord).
 */
export function useAlertes(site: string, page = 1, taille = 25, statut?: StatutAlerte) {
  const parametres = new URLSearchParams({
    site,
    page: String(page),
    taille: String(taille),
    ...(statut ? { statut } : {}),
  });
  return useQuery({
    queryKey: ['alertes', site, page, taille, statut ?? ''],
    queryFn: () => appelApi<EtatAlertes>(`/alertes?${parametres.toString()}`),
    placeholderData: (precedent) => precedent,
  });
}

/**
 * Pastille du menu « Alertes » : le compteur « Ouvertes à traiter » de la page,
 * sur la même antenne, pas les notifications non lues de la cloche (TF-492).
 * Sous la clé `alertes` : statuer une alerte la remet à jour.
 */
export function useAlertesOuvertes(site: string, active = true) {
  const parametres = new URLSearchParams({ site });
  return useQuery({
    queryKey: ['alertes', 'ouvertes', site],
    queryFn: () => appelApi<{ ouvertes: number }>(`/alertes/ouvertes?${parametres.toString()}`),
    enabled: active,
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['alertes'] });
  // « Paramétrage » montre les mêmes règles, et son historique les traces.
  void client.invalidateQueries({ queryKey: ['parametrage'] });
}

export function useModifierRegle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      code: string;
      actif?: boolean;
      canaux?: CanalNotification[];
      destinataires?: string[];
      reglages?: Record<string, number>;
    }) => {
      const { code, ...corps } = variables;
      return appelApi<{ code: string; message: string }>(`/alertes/regles/${code}`, {
        methode: 'PATCH',
        corps,
      });
    },
    onSuccess: () => invalider(client),
  });
}

/** Activation d'une règle sur un site précis (CDC §11). */
export function useActiverSurSite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { code: string; site: string; actif: boolean }) =>
      appelApi<{ message: string }>(
        `/alertes/regles/${variables.code}/sites/${variables.site}`,
        { methode: 'PUT', corps: { actif: variables.actif } },
      ),
    onSuccess: () => invalider(client),
  });
}

export function useActiverToutesLesRegles() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { site: string; actif: boolean }) =>
      appelApi<{ nombre: number; message: string }>('/alertes/regles/activation', {
        methode: 'POST',
        corps: variables,
      }),
    onSuccess: () => invalider(client),
  });
}

export function useStatuerAlerte() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; statut: 'TRAITEE' | 'IGNOREE' }) =>
      appelApi<{ message: string }>(`/alertes/${variables.id}`, {
        methode: 'PATCH',
        corps: { statut: variables.statut },
      }),
    onSuccess: () => invalider(client),
  });
}

export interface ResultatPassage {
  dossiersExamines: number;
  alertesOuvertes: number;
  alertesInchangees: number;
  destinatairesPrevenus: number;
  reglesInactives: string[];
  reglesNonEvaluees: string[];
  execute: string;
}

/** Passage immédiat du moteur, en plus du passage quotidien de 6 h. */
export function useDeclencherPassage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => appelApi<ResultatPassage>('/alertes/passage', { methode: 'POST' }),
    onSuccess: () => invalider(client),
  });
}
